import React, { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import PhotoPlane from "@/components/scene/PhotoPlane";
import VolumetricSmoke from "@/components/scene/VolumetricSmoke";
import { FireParticles, WaterSpray } from "@/components/scene/Hazards";

// Fire that ignites at the impact gash and grows as impactT ramps toward 1.
function ImpactFire({ getT, position, count = 60, scale = 1 }) {
  const g = useRef();
  useFrame(() => {
    if (!g.current) return;
    const on = Math.min(1, Math.max(0, (getT() - 0.15) * 1.6));
    g.current.visible = on > 0.02;
    g.current.scale.setScalar(0.4 + on * scale);
  });
  return (
    <group ref={g} position={position} visible={false}>
      <FireParticles count={count} scale={1} intensity={1} />
    </group>
  );
}

function Book({ position }) {
  return (
    <group position={position}>
      <mesh position={[0, 0.03, 0]}>
        <boxGeometry args={[0.3, 0.05, 0.42]} />
        <meshStandardMaterial color={0x1e1a14} roughness={0.5} />
      </mesh>
      <mesh position={[0, 0.061, 0]}>
        <boxGeometry args={[0.27, 0.012, 0.39]} />
        <meshStandardMaterial color={0xf2ede0} roughness={0.9} />
      </mesh>
      {/* gold cross — this is Stanley's Bible */}
      <mesh position={[0, 0.058, -0.06]}>
        <boxGeometry args={[0.02, 0.008, 0.14]} />
        <meshStandardMaterial color={0xc9a24b} metalness={0.7} roughness={0.3} emissive={0x8a6a1f} emissiveIntensity={0.4} />
      </mesh>
      <mesh position={[0, 0.058, -0.06]}>
        <boxGeometry args={[0.08, 0.008, 0.02]} />
        <meshStandardMaterial color={0xc9a24b} metalness={0.7} roughness={0.3} emissive={0x8a6a1f} emissiveIntensity={0.4} />
      </mesh>
    </group>
  );
}

function Desk({ position, hero }) {
  const mat = useMemo(() => new THREE.MeshStandardMaterial({ color: hero ? 0x3a2c22 : 0x2f2f33, roughness: 0.5, metalness: 0.2 }), [hero]);
  return (
    <group position={position}>
      <mesh material={mat} position={[0, 0.74, 0]}><boxGeometry args={[1.5, 0.06, 0.8]} /></mesh>
      {[[-0.68, -0.34], [0.68, -0.34], [-0.68, 0.34], [0.68, 0.34]].map((p, i) => (
        <mesh key={i} material={mat} position={[p[0], 0.37, p[1]]}><boxGeometry args={[0.07, 0.74, 0.07]} /></mesh>
      ))}
      {hero && <Book position={[0.1, 0.77, 0]} />}
    </group>
  );
}

// Mesh-fracture shell — intact at impactT=0, bursts apart as impactT→1.
function FractureShell({ getT }) {
  const grp = useRef();
  const shards = useMemo(() => {
    const arr = [];
    const CX = 0.9, CY = 2.3, TILT = -0.34; // gash centre + diagonal tilt (matches damage diagram)
    for (let i = 0; i < 34; i++) {
      const u = (i / 33) * 2 - 1; // position along the gash
      const alongX = u * 6.6;
      const alongY = alongX * Math.tan(TILT);
      const spread = 0.7 + 0.7 * (u + 1); // entry hole widens toward the east (+u) side
      const ang = Math.random() * Math.PI * 2;
      const r = 2 + Math.random() * 3;
      arr.push({
        home: [
          CX + alongX + (Math.random() - 0.5) * spread,
          CY + alongY + (Math.random() - 0.5) * spread * 0.8,
          -3.4 - Math.random() * 1.2,
        ],
        dir: [Math.cos(ang) * r, Math.random() * r * 0.6, (Math.random() - 0.2) * r],
        rot: [Math.random() * 6, Math.random() * 6, Math.random() * 6],
        size: [0.3 + Math.random() * 0.7, 0.2 + Math.random() * 0.6, 0.1 + Math.random() * 0.3],
      });
    }
    return arr;
  }, []);
  const mat = useMemo(() => new THREE.MeshStandardMaterial({ color: 0xdad6cc, roughness: 0.9 }), []);
  useFrame((state, dt) => {
    const t = getT();
    if (!grp.current) return;
    grp.current.children.forEach((c, i) => {
      const s = shards[i];
      c.position.set(
        s.home[0] + s.dir[0] * t,
        s.home[1] + s.dir[1] * t,
        s.home[2] + s.dir[2] * t
      );
      c.rotation.set(s.rot[0] * t, s.rot[1] * t, s.rot[2] * t);
      c.visible = t > 0.01;
    });
  });
  return (
    <group ref={grp}>
      {shards.map((s, i) => (
        <mesh key={i} material={mat} visible={false}>
          <boxGeometry args={s.size} />
        </mesh>
      ))}
    </group>
  );
}

function Flight175() {
  return (
    <group>
      <mesh rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.22, 0.14, 2.2, 10]} />
        <meshStandardMaterial color={0xd8dde3} metalness={0.5} roughness={0.4} />
      </mesh>
      <mesh position={[0.9, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
        <coneGeometry args={[0.14, 0.4, 10]} />
        <meshStandardMaterial color={0xc2c7cd} metalness={0.5} roughness={0.4} />
      </mesh>
      {/* wings */}
      <mesh position={[-0.1, 0, 0]}>
        <boxGeometry args={[0.5, 0.04, 2.4]} />
        <meshStandardMaterial color={0xb8bdc4} metalness={0.4} roughness={0.5} />
      </mesh>
      {/* tail */}
      <mesh position={[-1.0, 0.25, 0]}>
        <boxGeometry args={[0.4, 0.5, 0.04]} />
        <meshStandardMaterial color={0xb8bdc4} metalness={0.4} roughness={0.5} />
      </mesh>
      <mesh position={[-1.0, 0, 0]}>
        <boxGeometry args={[0.35, 0.04, 1.0]} />
        <meshStandardMaterial color={0xb8bdc4} metalness={0.4} roughness={0.5} />
      </mesh>
    </group>
  );
}

function StanleyOffice({ game }) {
  const key = useRef();
  const plane = useRef();
  const START = useMemo(() => new THREE.Vector3(24, 12, -26), []);
  const END = useMemo(() => new THREE.Vector3(1.4, 3.0, -4.4), []);
  useFrame((state) => {
    const t = game.current.impactT;
    if (key.current) key.current.intensity = 1.1 * (1 - t) + 0.1;
    if (plane.current) {
      // approach: idle far when t~0, rush toward the window as impact begins
      const approach = t < 0.02 ? 0.06 + (Math.sin(state.clock.elapsedTime * 0.6) * 0.5 + 0.5) * 0.04 : Math.min(1, t / 0.5);
      plane.current.position.lerpVectors(START, END, approach);
      plane.current.scale.setScalar(0.5 + approach * 1.1);
      // sustained steep ~38deg left bank + diagonal nose-down entry matching the damage diagram
      const bank = 0.66 * Math.min(1, 0.28 + approach * 1.1);
      plane.current.rotation.set(-0.14, -0.7, -bank);
      plane.current.visible = t < 0.52;
    }
  });
  return (
    <group>
      <ambientLight color={0xf3f0e8} intensity={0.9} />
      <directionalLight ref={key} color={0xfff4dd} intensity={1.1} position={[4, 7, 6]} />
      {/* floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[26, 20]} />
        <meshStandardMaterial color={0xcfc9bd} roughness={0.8} />
      </mesh>
      {/* window wall (south-facing) with the archival WTC skyline view */}
      <mesh position={[0, 2.2, -4.5]}>
        <planeGeometry args={[16, 5]} />
        <meshStandardMaterial color={0xdfeaf5} emissive={0xbcd4ec} emissiveIntensity={0.4} />
      </mesh>
      <PhotoPlane url="/archival/office_window.png" args={[15.2, 4.6]} position={[0, 2.2, -4.42]} emissive tone={false} />
      {/* mullions */}
      {[-6, -3, 0, 3, 6].map((x) => (
        <mesh key={x} position={[x, 2.2, -4.35]}><boxGeometry args={[0.1, 5, 0.1]} /><meshStandardMaterial color={0x8a8578} /></mesh>
      ))}
      {/* United Flight 175 — 3D model banking toward the 81st-floor window */}
      <group ref={plane}>
        <Flight175 />
      </group>
      {/* desks */}
      <Desk position={[0.4, 0, -1]} hero />
      <Desk position={[-3, 0, -0.5]} />
      <Desk position={[3.2, 0, -0.5]} />
      <Desk position={[-2.5, 0, 1.8]} />
      {/* enclosing office walls + ceiling — archival photo mapped onto 3D geometry */}
      <PhotoPlane url="/archival/devastated_office.png" args={[20, 5]} position={[-8, 2.5, -1]} rotation={[0, Math.PI / 2, 0]} />
      <PhotoPlane url="/archival/devastated_office.png" args={[20, 5]} position={[8, 2.5, -1]} rotation={[0, -Math.PI / 2, 0]} />
      <mesh position={[0, 5, -1]} rotation={[Math.PI / 2, 0, 0]}>
        <planeGeometry args={[16, 20]} />
        <meshStandardMaterial color={0xbdb6a6} roughness={0.9} side={THREE.DoubleSide} />
      </mesh>
      <FractureShell getT={() => game.current.impactT} />
      {/* fire blooming from the 81st-floor gash where Flight 175 tore through */}
      <ImpactFire getT={() => game.current.impactT} position={[0.9, 1.4, -3.7]} count={70} scale={1.3} />
      <ImpactFire getT={() => game.current.impactT} position={[-2.6, 1.1, -3.9]} count={44} scale={0.9} />
      <ImpactFire getT={() => game.current.impactT} position={[3.4, 1.1, -3.9]} count={44} scale={0.9} />
      {/* dense smoke fills the office (including where Stanley stood) from the first frame */}
      <VolumetricSmoke count={26} area={[12, 4, 3]} center={[0, 2.4, -3]} color={0x24211b} opacity={0.8} rise={0.32} getIntensity={() => 0.9} />
      {/* impact plume rising from the exact spot where Stanley stood — blooms as the plane hits, then keeps drifting */}
      <VolumetricSmoke count={10} area={[1.6, 2.4, 1.6]} center={[0.4, 1.2, 0.4]} color={0x2a2620} opacity={0.85} rise={0.45} getIntensity={() => Math.min(1, Math.max(0, game.current.impactT - 0.1) * 2)} />
      {/* sprinkler burst — soaks the impacted floor for ~5s, then stops */}
      <WaterSpray game={game} position={[0.6, 4.4, -1.6]} count={80} scale={1.4} />
    </group>
  );
}

// Static rubble where Brian's walking figure used to be (fixed layout, no animation).
const HALLWAY_DEBRIS = [
  [-0.6, 0.12, 0.9, 0.5, 0.24, 0.4, 0.3], [0.4, 0.09, 0.4, 0.6, 0.18, 0.35, -0.5],
  [0.1, 0.06, 1.4, 0.9, 0.12, 0.25, 0.9], [-0.2, 0.15, -0.3, 0.45, 0.3, 0.45, 0.2],
  [0.8, 0.05, 1.1, 0.35, 0.1, 0.6, 1.2], [-0.9, 0.08, 0.1, 0.7, 0.16, 0.3, -0.8],
  [0.3, 0.2, 0.6, 0.3, 0.4, 0.3, 0.6],
];

function HallwayDebris() {
  return (
    <group>
      {HALLWAY_DEBRIS.map(([x, y, z, w, h, d, r], i) => (
        <mesh key={i} position={[x, y, z]} rotation={[0.15 * r, r, 0.1 * r]}>
          <boxGeometry args={[w, h, d]} />
          <meshStandardMaterial color={i % 2 ? 0x5a554c : 0x3d3a34} roughness={0.95} />
        </mesh>
      ))}
    </group>
  );
}

function BrianHallway({ game }) {
  return (
    <group>
      <ambientLight color={0xdcd6c4} intensity={1.05} />
      <hemisphereLight args={[0xfff2d0, 0x2a2a22, 0.7]} />
      <fogExp2 attach="fog" args={[0x1c1a14, 0.05]} />
      {/* carpet */}
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[6, 30]} />
        <meshStandardMaterial color={0x2b3226} roughness={0.95} />
      </mesh>
      {/* marble side walls */}
      {[-2.6, 2.6].map((x) => (
        <mesh key={x} position={[x, 2, -4]}>
          <boxGeometry args={[0.2, 4, 30]} />
          <meshStandardMaterial color={0x25302a} roughness={0.35} metalness={0.35} />
        </mesh>
      ))}
      {/* ceiling */}
      <mesh position={[0, 4, -4]} rotation={[Math.PI / 2, 0, 0]}>
        <planeGeometry args={[6, 30]} />
        <meshStandardMaterial color={0xcfc7b2} roughness={0.8} />
      </mesh>
      {/* ceiling lights */}
      {[-6, -2, 2, 6, 10].map((z) => (
        <group key={z} position={[0, 3.9, -z]}>
          <mesh><boxGeometry args={[1.2, 0.08, 0.6]} /><meshStandardMaterial color={0xfff4d6} emissive={0xffe9a8} emissiveIntensity={1.3} /></mesh>
          <pointLight color={0xffe9c0} intensity={0.9} distance={7} />
        </group>
      ))}
      {/* no figures in the opening hallway: smoke and debris fill the spot where Brian's model used to walk */}
      <HallwayDebris />
      <VolumetricSmoke count={14} area={[3, 2.2, 3]} center={[0, 1.3, 0.6]} color={0x1e1b16} opacity={0.75} rise={0.25} getIntensity={() => 0.9} />
      {/* impact plume rising from the exact spot where Brian walked — blooms as the plane hits, then keeps drifting */}
      <VolumetricSmoke count={10} area={[1.6, 2.4, 1.6]} center={[0, 1.2, 0.6]} color={0x272319} opacity={0.85} rise={0.45} getIntensity={() => Math.min(1, Math.max(0, game.current.impactT - 0.1) * 2)} />
      <FractureShell getT={() => game.current.impactT * 0.6} />
      {/* fire spreading down the hallway as the ceiling gives way */}
      <ImpactFire getT={() => game.current.impactT} position={[0.7, 1.0, -4.2]} count={50} scale={1.0} />
      <ImpactFire getT={() => game.current.impactT} position={[-0.8, 1.0, -6.5]} count={40} scale={0.85} />
      <VolumetricSmoke count={24} area={[5, 4, 10]} center={[0, 2.4, -4]} color={0x201d17} opacity={0.8} rise={0.3} getIntensity={() => 0.9} />
      {/* sprinkler burst — soaks the hallway for ~5s, then stops */}
      <WaterSpray game={game} position={[0, 3.6, -4]} count={70} scale={1.6} />
    </group>
  );
}

export default function PrologueScene({ game, pov }) {
  return pov === "brian" ? <BrianHallway game={game} /> : <StanleyOffice game={game} />;
}
